/**
 * NOS-40: Page settings panel for multi-page website builder.
 * Edit slug, title, SEO meta, publish status for the current page.
 * Dark-themed to match builder panel aesthetic.
 */

import { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import type { Json } from "@/types/database"
import { usePage, useUpdatePage, usePublishPage } from "@/hooks/usePages"
import { useCustomizationStore } from "@/stores/customizationStore"
import { toast } from "sonner"
import { trackEvent } from "@/lib/posthog"
import { TinyInput } from "./TinyInput"

export interface PageSettingsPanelProps {
  orgId: string | null
  pageId: string | null
  onSettingsChange?: () => void
}

interface PageMeta {
  title?: string
  description?: string
  og_image?: string
}

const LABEL_STYLE: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 600,
  textTransform: "uppercase",
  color: "rgba(255,255,255,0.35)",
  marginBottom: 6,
  display: "block",
  letterSpacing: "0.05em",
}

const SECTION_STYLE: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
}

const DIVIDER_STYLE: React.CSSProperties = {
  height: 1,
  background: "rgba(255,255,255,0.06)",
  margin: "4px 0",
}

export function PageSettingsPanel({
  orgId,
  pageId,
  onSettingsChange,
}: PageSettingsPanelProps) {
  const { page, loading, refresh } = usePage(orgId, pageId)
  const { updatePage, loading: updating } = useUpdatePage(orgId)
  const { publishPage, loading: publishing } = usePublishPage(orgId)
  const load = useCustomizationStore((s) => s.load)

  const [slug, setSlug] = useState("")
  const [title, setTitle] = useState("")
  const [metaTitle, setMetaTitle] = useState("")
  const [metaDescription, setMetaDescription] = useState("")
  const [ogImage, setOgImage] = useState("")

  useEffect(() => {
    if (page) {
      setSlug(page.slug ?? "")
      setTitle(page.title ?? "")
      // NOS-181: seo_title/seo_description columns (populated from source
      // metadata at import) win; legacy meta.title/description JSONB overrides
      // fill in for pre-NOS-181 pages.
      const meta = (page.meta as PageMeta | null) ?? {}
      setMetaTitle(page.seo_title ?? meta.title ?? "")
      setMetaDescription(page.seo_description ?? meta.description ?? "")
      // NOS-249: og_image lives in meta JSONB (written by import, NOS-241) and
      // is now editable here as the social share image override.
      setOgImage(typeof meta.og_image === "string" ? meta.og_image : "")
    }
  }, [page])

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!pageId || !orgId) return

    const newSlug = slug.trim().toLowerCase().replace(/\s+/g, "-")
    if (!newSlug) {
      toast.error("Slug is required")
      return
    }
    if (newSlug === "home" && page?.slug !== "home") {
      toast.error("Slug 'home' is reserved")
      return
    }

    // NOS-181: SEO lives in the seo_title/seo_description columns now. The
    // legacy meta.title/description keys are retired on save (so a cleared
    // field cannot resurface an old override) while every other meta key
    // (e.g. og_image from import, NOS-241) is preserved untouched.
    const meta =
      page?.meta && typeof page.meta === "object" && !Array.isArray(page.meta)
        ? ({ ...(page.meta as Record<string, unknown>) } as Record<string, unknown>)
        : {}
    delete meta.title
    delete meta.description
    // NOS-249: og_image override stays in meta JSONB; a cleared field removes
    // it so the generated branded default takes over.
    const ogImageUrl = ogImage.trim()
    if (ogImageUrl) {
      meta.og_image = ogImageUrl
    } else {
      delete meta.og_image
    }

    const ok = await updatePage(pageId, {
      slug: newSlug,
      title: title.trim() || null,
      seo_title: metaTitle.trim() || null,
      seo_description: metaDescription.trim() || null,
      meta: meta as Json,
    })
    if (ok) {
      await load(orgId, pageId)
      refresh()
      onSettingsChange?.()
      trackEvent("page_settings_updated", { org_id: orgId, slug: newSlug })
      toast.success("Settings saved")
    } else {
      toast.error("Failed to save settings")
    }
  }

  const handlePublishToggle = async () => {
    if (!pageId || !orgId) return
    const next = !page?.is_published
    const ok = await publishPage(pageId, next)
    if (ok) {
      await load(orgId, pageId)
      refresh()
      onSettingsChange?.()
      trackEvent(next ? "page_published" : "page_unpublished", {
        org_id: orgId,
        page_id: pageId,
      })
      toast.success(next ? "Page published" : "Page unpublished")
    } else {
      toast.error("Failed to update publish status")
    }
  }

  if (!pageId) {
    return (
      <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
        Select a page to edit its settings.
      </p>
    )
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
        <Loader2 style={{ width: 20, height: 20, color: "rgba(255,255,255,0.3)", animation: "spin 1s linear infinite" }} />
      </div>
    )
  }

  if (!page) {
    return (
      <p style={{ fontSize: 12, color: "#f87171" }}>
        Page not found.
      </p>
    )
  }

  const isHome = page.slug === "home"

  return (
    <form onSubmit={handleSave} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Page identity */}
      <div style={SECTION_STYLE}>
        <div>
          <label style={LABEL_STYLE}>URL slug</label>
          <TinyInput
            data-testid="page-settings-slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="e.g. about-us"
            disabled={isHome}
            style={{ fontFamily: "monospace", opacity: isHome ? 0.5 : 1 }}
          />
          {isHome && (
            <p style={{ marginTop: 4, fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
              Home page slug cannot be changed.
            </p>
          )}
        </div>
        <div>
          <label style={LABEL_STYLE}>Page title</label>
          <TinyInput
            data-testid="page-settings-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. About Us"
          />
        </div>
      </div>

      <div style={DIVIDER_STYLE} />

      {/* SEO */}
      <div style={SECTION_STYLE}>
        <p style={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.5)", margin: 0 }}>
          SEO (optional)
        </p>
        <div>
          <label style={LABEL_STYLE}>Meta title</label>
          <TinyInput
            value={metaTitle}
            onChange={(e) => setMetaTitle(e.target.value)}
            placeholder="Override for search results"
          />
        </div>
        <div>
          <label style={LABEL_STYLE}>Meta description</label>
          <TinyInput
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            placeholder="Brief description for search results"
            multiline
          />
        </div>
        <div>
          <label style={LABEL_STYLE}>Social share image</label>
          <TinyInput
            data-testid="page-settings-og-image"
            value={ogImage}
            onChange={(e) => setOgImage(e.target.value)}
            placeholder="https://… (blank = branded default)"
          />
          <p style={{ marginTop: 4, fontSize: 11, color: "rgba(255,255,255,0.3)" }}>
            Shown when this page is shared. Leave blank for an auto-generated branded image.
          </p>
        </div>
      </div>

      <div style={DIVIDER_STYLE} />

      {/* Publish status */}
      <div>
        <label style={LABEL_STYLE}>Publish status</label>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 12, color: page.is_published ? "#4ade80" : "rgba(255,255,255,0.4)" }}>
            {page.is_published ? "Published" : "Draft"}
          </span>
          <button
            type="button"
            data-testid="page-settings-publish-toggle"
            onClick={handlePublishToggle}
            disabled={publishing}
            style={{
              padding: "5px 12px",
              borderRadius: 7,
              border: `1px solid ${page.is_published ? "rgba(255,255,255,0.15)" : "rgba(255,255,255,0.2)"}`,
              background: page.is_published ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.1)",
              color: "rgba(255,255,255,0.7)",
              fontSize: 11,
              cursor: publishing ? "default" : "pointer",
              opacity: publishing ? 0.6 : 1,
            }}
          >
            {publishing ? "…" : page.is_published ? "Unpublish" : "Publish"}
          </button>
        </div>
      </div>

      <div style={DIVIDER_STYLE} />

      {/* Save */}
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button
          type="submit"
          disabled={updating}
          style={{
            padding: "7px 16px",
            borderRadius: 8,
            border: "none",
            background: updating ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.12)",
            color: updating ? "rgba(255,255,255,0.4)" : "#fff",
            fontSize: 12,
            fontWeight: 600,
            cursor: updating ? "default" : "pointer",
          }}
        >
          {updating ? "Saving…" : "Save settings"}
        </button>
      </div>
    </form>
  )
}
